import { Box, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { ReactElement } from "react";
import {
  BidNotificationMetaDto,
  ContractDateReminderType,
  ContractDocumentNotificationMetaDto,
  ContractNotificationMetaDto,
  ContractReminderNotificationMetaDto,
  ContractTaskNotificationMetaDto,
  NotificationDto,
  NotificationTypeDto,
  SeV2DoffinPublicationFailedNotificationMetaDto,
  SeV2DoffinPublishedNotificationMetaDto,
  SeV2InvitedToTenderNotificationMetaDto,
  SeV2OrganizationInviteNotificationMetaDto,
  SeV2DpsAdmittedNotificationMetaDto,
  SeV2QualificationSchemeAdmittedNotificationMetaDto,
  SeV2QualificationSchemeRemovedNotificationMetaDto,
  SeV2DpsRemovedNotificationMetaDto,
  SeV2RequestNotQualifiedNotificationMetaDto,
  SeV2RequestNotSelectedNotificationMetaDto,
  SeV2ResponsibleBuyerChangedNotificationMetaDto,
  SourcingEventAnnouncementNotificationMetaDto,
  SourcingEventNotificationMetaDto,
  SourcingEventQuestionCommentNotificationMetaDto,
  SourcingEventQuestionNotificationMetaDto,
} from "../../autogen/bff-api";
import { displayDate, type SupportedLocale } from "../../common/formatting/displayDate";
import { displayWrappedWithEllipsis } from "../../common/formatting/displayWrappedWithEllipsis";

const TaskMetaContext = ({ meta }: { meta: ContractTaskNotificationMetaDto }): ReactElement | null => {
  if (!meta.projectName && !meta.counterpartyName) {
    return null;
  }
  return (
    <Text fontSize={"xs"} color={"smMuted"} mt={"0.5"}>
      {meta.projectName ? (
        <>
          {t("Project")}: <b>{displayWrappedWithEllipsis(meta.projectName, 60)}</b>
        </>
      ) : null}
      {meta.projectName && meta.counterpartyName ? " · " : ""}
      {meta.counterpartyName ? (
        <>
          {t("Counterparty")}: <b>{displayWrappedWithEllipsis(meta.counterpartyName, 60)}</b>
        </>
      ) : null}
    </Text>
  );
};

export const getNotificationDescription = (
  notification: NotificationDto,
  locale?: SupportedLocale | null,
): ReactElement => {
  switch (notification.type) {
    case NotificationTypeDto.ContractTaskDelegated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("delegated the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("to you for the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskStatusUpdated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("changed the status for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractDateReminder: {
      const meta = requireContractReminderMeta(notification);

      const getNotificationName = (): string => {
        switch (meta.reminderType) {
          case ContractDateReminderType.EndDateReminder:
            return t("The end date");
          case ContractDateReminderType.RenewalDateReminder:
            return t("renewal date");
          case ContractDateReminderType.WarrantyExpirationDateReminder:
            return t("warranty expiration date");
          case ContractDateReminderType.StartDateReminder:
            return t("start date");
          case ContractDateReminderType.TerminationDateReminder:
            return t("termination date");
          case ContractDateReminderType.NoticePeriodReminder:
            return t("start of notice period");
          case ContractDateReminderType.OptionExecutionDeadlineReminder:
            return t("option execution deadline");
          case ContractDateReminderType.OptionStartDateReminder:
            return t("option start date");
          case ContractDateReminderType.NoEndDateReminder:
            return t("no end date");
        }
      };

      if (meta.reminderType === "NoEndDateReminder") {
        return (
          <Box>
            <Text fontSize={"sm"}>
              {t("You have an active contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}</b>{" "}
              {t("without an end date created the")} <b>{displayDate(meta.date, locale)}</b>.
            </Text>
          </Box>
        );
      }

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The")} <b>{getNotificationName()}</b> {t("for the contract")}
            {" '"}
            {displayWrappedWithEllipsis(meta.contractName, 100)}
            {"' "}
            {t("is approaching at the")} <b>{displayDate(meta.date, locale)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractDocumentCompletedSigning: {
      const meta = requireContractDocumentMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The document")} <b>{displayWrappedWithEllipsis(meta.documentName, 100)} </b>
            {t("for the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("has completed signing!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSignatureDeadlineExpired: {
      const meta = requireContractDocumentMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The deadline for signing the document")} <b>{displayWrappedWithEllipsis(meta.documentName, 100)} </b>
            {t("belonging to the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("has expired")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithDepartment: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with your department")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithOrg: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with your organization")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractSharedWithPerson: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("shared the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)} </b>
            {t("with you")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractMessageReceived: {
      const meta = requireContractkMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("sent a message in the contract")}{" "}
            <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskCommentAdded: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("added a comment for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDeleted: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("deleted the task")} <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDueDateUpdated: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.triggeredByFirstName} {t("changed the due date for the task")}{" "}
            <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.ContractTaskDueDateReminder: {
      const meta = requireTaskMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The due date is approaching for the task")} <b>{displayWrappedWithEllipsis(meta.taskName, 100)} </b>
            {t("in the contract")} <b>{displayWrappedWithEllipsis(meta.contractName, 100)}.</b>
          </Text>
          <TaskMetaContext meta={meta} />
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventAnnouncement: {
      const meta = requireSourcingEventAnnouncementMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has a new announcement!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventAwarded: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b> {t("has been awarded!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventNewQuestion: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A new question has been asked for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2NewQuestion: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A new question has been asked for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QuestionAnswered: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your question has been answered")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ClarificationMessageToSupplier: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The buyer has sent you a message in a clarification dialog")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ClarificationMessageToBuyer: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A tenderer has replied in a clarification dialog")}:{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2InformationPublished: {
      const meta = requireSourcingEventAnnouncementMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("New information has been published for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2AwardLetterDispatched: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been awarded!")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2InvitedToTender: {
      const meta = requireSeV2InvitedToTenderMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b>{" "}
            {t("has invited you to tender in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2OrganizationInvite: {
      const meta = requireSeV2OrganizationInviteMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b>{" "}
            {t("has invited you to tender in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RequestNotQualified: {
      const meta = requireSeV2RequestNotQualifiedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Your request to participate in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("was not qualified")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DpsAdmitted: {
      const meta = requireSeV2DpsAdmittedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You are qualified in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("You will be invited to every competition under the system.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QualificationSchemeAdmitted: {
      const meta = requireSeV2QualificationSchemeAdmittedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You are qualified in")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("You are on the supplier list and may be invited to competitions under the system.")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2QualificationSchemeRemoved: {
      const meta = requireSeV2QualificationSchemeRemovedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You have been removed from")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {meta.removalReason}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DpsRemoved: {
      const meta = requireSeV2DpsRemovedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You have been removed from")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {meta.removalReason}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RequestNotSelected: {
      const meta = requireSeV2RequestNotSelectedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("Not selected for tender")}: <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>.{" "}
            {t("Your organization was qualified, but not among the suppliers invited to tender.")}
            {meta.selectionReason ? ` ${meta.selectionReason}` : ""}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2AwardDecisionRevoked: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The award decision for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been withdrawn")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidDeadlineReminder: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The bid delivery deadline for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)} </b>
            {t("is approaching")}{" "}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2ResponsibleBuyerChanged: {
      const meta = requireSeV2ResponsibleBuyerChangedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The responsible buyer for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("changed from")} <b>{meta.previousResponsibleName}</b> {t("to")} <b>{meta.newResponsibleName}</b>.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventCancelled: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been cancelled")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventClosed: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been closed")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2BidDelivered: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.supplierOrganizationName}</b> {t("has delivered a bid for")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2RoundPublished: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            <b>{meta.eventOwningOrganizationName}</b> {t("has invited you to a negotiation round in")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2EventChanged: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been changed")}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DoffinPublished: {
      const meta = requireSeV2DoffinPublishedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.isChangeNotice ? t("The change notice for") : t("The contract notice for")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b> {t("has been published on Doffin")}.
          </Text>
          <Text fontSize={"xs"} color={"smMuted"} mt={"0.5"}>
            {t("Doffin reference")}: <b>{meta.doffinReference}</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SeV2DoffinPublicationFailed: {
      const meta = requireSeV2DoffinPublicationFailedMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {meta.isChangeNotice ? t("The change notice for") : t("The contract notice for")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b> {t("was not published on Doffin")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventNewQuestionComment: {
      const meta = requireSourcingEventQuestionCommentMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A new comment has been added to the question")}{" "}
            <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)} </b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventOrganizationInvite: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You have been invited to deliver a bid for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)} </b>
            {t("by")} <b>{displayWrappedWithEllipsis(meta.eventOwningOrganizationName, 100)}.</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventCancelled: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been cancelled")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventClosed: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The sourcing event")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)}</b>{" "}
            {t("has been completed")}.
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.NegotiationInvite: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("You have been invited to deliver an updated bid for the sourcing event")}{" "}
            <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)} </b>
            {t("by")} <b>{displayWrappedWithEllipsis(meta.eventOwningOrganizationName, 100)}.</b>
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.SourcingEventQuestionStatusUpdated: {
      const meta = requireSourcingEventQuestionMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The status of your question")} <b>{displayWrappedWithEllipsis(meta.questionSubject, 100)} </b>
            {t("has been updated")}{" "}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.BidDeliveryDeadline: {
      const meta = requireSourcingEventMeta(notification);

      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("The bid delivery deadline for")} <b>{displayWrappedWithEllipsis(meta.eventTitle, 100)} </b>
            {t("is approaching")}{" "}
          </Text>
        </Box>
      );
    }
    case NotificationTypeDto.BidDelivered: {
      const meta = requireBidMeta(notification);
      return (
        <Box>
          <Text fontSize={"sm"}>
            {t("A bid was delivered for")} <b>{meta.eventTitle}</b>.
          </Text>
        </Box>
      );
    }
  }
};

export const requireTaskMeta = (notification: NotificationDto): ContractTaskNotificationMetaDto => {
  if (!notification.meta.contractTask) {
    throw new Error("should never happen");
  }
  return notification.meta.contractTask;
};

export const requireContractkMeta = (notification: NotificationDto): ContractNotificationMetaDto => {
  if (!notification.meta.contract) {
    throw new Error("should never happen");
  }
  return notification.meta.contract;
};

export const requireContractReminderMeta = (notification: NotificationDto): ContractReminderNotificationMetaDto => {
  if (!notification.meta.contractReminder) {
    throw new Error("should never happen");
  }
  return notification.meta.contractReminder;
};

export const requireContractDocumentMeta = (notification: NotificationDto): ContractDocumentNotificationMetaDto => {
  if (!notification.meta.contractDocument) {
    throw new Error("should never happen");
  }
  return notification.meta.contractDocument;
};

export const requireSourcingEventMeta = (notification: NotificationDto): SourcingEventNotificationMetaDto => {
  if (!notification.meta.sourcingEvent) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEvent;
};

export const requireSeV2InvitedToTenderMeta = (
  notification: NotificationDto
): SeV2InvitedToTenderNotificationMetaDto => {
  if (!notification.meta.seV2InvitedToTender) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2InvitedToTender;
};

export const requireSeV2OrganizationInviteMeta = (
  notification: NotificationDto
): SeV2OrganizationInviteNotificationMetaDto => {
  if (!notification.meta.seV2OrganizationInvite) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2OrganizationInvite;
};

export const requireSeV2RequestNotQualifiedMeta = (
  notification: NotificationDto
): SeV2RequestNotQualifiedNotificationMetaDto => {
  if (!notification.meta.seV2RequestNotQualified) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2RequestNotQualified;
};

export const requireSeV2DpsAdmittedMeta = (
  notification: NotificationDto
): SeV2DpsAdmittedNotificationMetaDto => {
  if (!notification.meta.seV2DpsAdmitted) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DpsAdmitted;
};

export const requireSeV2QualificationSchemeAdmittedMeta = (
  notification: NotificationDto
): SeV2QualificationSchemeAdmittedNotificationMetaDto => {
  if (!notification.meta.seV2QualificationSchemeAdmitted) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2QualificationSchemeAdmitted;
};

export const requireSeV2QualificationSchemeRemovedMeta = (
  notification: NotificationDto
): SeV2QualificationSchemeRemovedNotificationMetaDto => {
  if (!notification.meta.seV2QualificationSchemeRemoved) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2QualificationSchemeRemoved;
};

export const requireSeV2DpsRemovedMeta = (
  notification: NotificationDto
): SeV2DpsRemovedNotificationMetaDto => {
  if (!notification.meta.seV2DpsRemoved) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DpsRemoved;
};

export const requireSeV2RequestNotSelectedMeta = (
  notification: NotificationDto
): SeV2RequestNotSelectedNotificationMetaDto => {
  if (!notification.meta.seV2RequestNotSelected) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2RequestNotSelected;
};

export const requireSeV2ResponsibleBuyerChangedMeta = (
  notification: NotificationDto
): SeV2ResponsibleBuyerChangedNotificationMetaDto => {
  if (!notification.meta.seV2ResponsibleBuyerChanged) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2ResponsibleBuyerChanged;
};

export const requireSeV2DoffinPublishedMeta = (
  notification: NotificationDto
): SeV2DoffinPublishedNotificationMetaDto => {
  if (!notification.meta.seV2DoffinPublished) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DoffinPublished;
};

export const requireSeV2DoffinPublicationFailedMeta = (
  notification: NotificationDto
): SeV2DoffinPublicationFailedNotificationMetaDto => {
  if (!notification.meta.seV2DoffinPublicationFailed) {
    throw new Error("should never happen");
  }
  return notification.meta.seV2DoffinPublicationFailed;
};

export const requireBidMeta = (notification: NotificationDto): BidNotificationMetaDto => {
  if (!notification.meta.bid) throw new Error("Notification does not have bid meta");
  return notification.meta.bid;
};

export const requireSourcingEventQuestionMeta = (
  notification: NotificationDto
): SourcingEventQuestionNotificationMetaDto => {
  if (!notification.meta.sourcingEventQuestion) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEventQuestion;
};

export const requireSourcingEventQuestionCommentMeta = (
  notification: NotificationDto
): SourcingEventQuestionCommentNotificationMetaDto => {
  if (!notification.meta.sourcingEventQuestionComment) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEventQuestionComment;
};

export const requireSourcingEventAnnouncementMeta = (
  notification: NotificationDto
): SourcingEventAnnouncementNotificationMetaDto => {
  if (!notification.meta.sourcingEventAnnouncement) {
    throw new Error("should never happen");
  }
  return notification.meta.sourcingEventAnnouncement;
};
